import {
  Autocomplete,
  Box,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface FilterField {
  name: string;
  label: string;
  type: "autocomplete" | "text" | "date" | "number" | "choice" | "toggle";
  options?: { value: any; label: string }[];
}

interface GenericFilterProps {
  fields: FilterField[];
  filters: Record<string, any>;
  onFiltersChange: (filters: Record<string, any>) => void;
  onApplyFilters: (filters?: Record<string, any>) => void;
  onClearFilters: () => void;
}

const GenericFilter = ({
  fields,
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}: GenericFilterProps) => {
  const { t, i18n } = useTranslation();

  const onApplyFiltersRef = React.useRef(onApplyFilters);
  onApplyFiltersRef.current = onApplyFilters;

  const debouncedApplyFilters = useMemo(
    () => debounce((currentFilters: Record<string, any>) => onApplyFiltersRef.current(currentFilters), 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedApplyFilters.cancel();
    };
  }, [debouncedApplyFilters]);

  const setFilterValue = (name: string, value: any, immediate = false) => {
    const newFilters = {
      ...filters,
      [name]: value === "" || value === null ? undefined : value,
    };
    onFiltersChange(newFilters);
    
    if (immediate) {
      debouncedApplyFilters.cancel();
      onApplyFilters(newFilters);
    } else {
      debouncedApplyFilters(newFilters);
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event.target.name, event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      debouncedApplyFilters.cancel();
      onApplyFilters();
    }
  };

  const renderField = (field: FilterField) => {
    switch (field.type) {
      case "toggle":
        return (
          <Box key={field.name}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              {field.label}
            </Typography>
            <ToggleButtonGroup
              value={filters[field.name]}
              exclusive
              onChange={(_, value) => {
                if (value !== null) {
                  setFilterValue(field.name, value, true);
                }
              }}
              size="small"
              fullWidth
            >
              {field.options?.map((option) => (
                <ToggleButton key={option.value} value={option.value} sx={{ textTransform: "none" }}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        );
      case "choice":
      case "autocomplete":
        return (
          <Autocomplete
            key={field.name}
            options={field.options ?? []}
            getOptionLabel={(option) => option.label}
            value={
              field.options?.find(
                (opt) => opt.value === filters[field.name]
              ) ?? null
            }
            onChange={(_, newValue) => {
              setFilterValue(field.name, newValue?.value, true);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                fullWidth
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            noOptionsText={t("generic.no-options")}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
          />
        );
      case "number":
        return (
          <TextField
            key={field.name}
            name={field.name}
            label={field.label}
            type="number"
            value={filters[field.name] ?? ""}
            onChange={(e) => {
              const val = e.target.value === "" ? undefined : Number(e.target.value);
              setFilterValue(field.name, val);
            }}
            onKeyDown={handleKeyDown}
            fullWidth
            size="small"
          />
        );
      case "text":
        return (
          <TextField
            key={field.name}
            name={field.name}
            label={field.label}
            value={filters[field.name] ?? ""}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            fullWidth
            size="small"
          />
        );
      case "date": {
        const isEn = i18n.language === "en";
        return (
          <LocalizationProvider key={field.name} dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label={field.label}
              value={filters[field.name] ? dayjs(filters[field.name]) : null}
              format={isEn ? "DD-MM-YYYY hh:mm:ss A" : "DD-MM-YYYY HH:mm:ss"}
              ampm={isEn}
              onChange={(newValue) => {
                setFilterValue(field.name, newValue ? newValue.toISOString() : null, true);
              }}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </LocalizationProvider>
        );
      }
      default:
        return null;
    }
  };

  const hasActiveFilters = Object.entries(filters).some(([key, v]) => {
    const field = fields.find(f => f.name === key);
    if (field?.type === "toggle") return false; // Toggle is always active, don't count for clear button visibility if it's the only one
    return v !== undefined && v !== "";
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {fields.map((field) => (
        <Box key={field.name}>
          {renderField(field)}
        </Box>
      ))}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="text"
          color="secondary"
          onClick={onClearFilters}
          sx={{ textTransform: "none" }}
          disabled={!hasActiveFilters}
        >
          {t("generic.clear-filters")}
        </Button>
      </Box>
    </Box>
  );
};

export default GenericFilter;
